import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { ws, gbp, ROLE_LABELS } from "./api";
import { useWorkspace } from "./WorkspaceContext";
import { PageHeader, SubTabs, Panel, Empty, Loading } from "./ui";

const Tile = ({ label, value, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-heading text-2xl font-semibold text-[#0B1530] mt-2">{value}</p>
  </div>
);

const My = () => {
  const [r, setR] = useState(null);
  const [p, setP] = useState(null);
  useEffect(() => {
    ws("/crm/workspace/reports/my").then(setR).catch((e) => toast.error(e.message));
    ws("/crm/data/deals/pipeline").then(setP).catch(() => {});
  }, []);
  if (!r) return <Loading />;
  return (
    <div className="space-y-4" data-testid="my-reports">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="Deals I created" value={r.deals_created} testid="report-deals-created" />
        <Tile label="Won value" value={gbp(r.won_value)} testid="report-won-value" />
        <Tile label="Open pipeline" value={gbp(r.open_value)} testid="report-open-value" />
        <Tile label="Win rate" value={`${r.win_rate}%`} testid="report-win-rate" />
      </div>
      {p && (
        <Panel title="Organisation pipeline by stage" testid="report-pipeline-stages">
          <table className="w-full text-sm"><tbody>{p.stages.map((s) => (
            <tr key={s.id} className="border-b border-slate-50"><td className="py-2 flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: s.color }} />{s.name}</td><td className="text-right text-slate-500">{(p.by_stage[s.id] || []).length} deals</td><td className="text-right font-medium w-32">{gbp(p.totals[s.id])}</td></tr>
          ))}</tbody></table>
        </Panel>
      )}
    </div>
  );
};

const Team = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { ws("/crm/workspace/reports/team").then((r) => setRows(r.rows)).catch((e) => { toast.error(e.message); setRows([]); }); }, []);
  if (!rows) return <Loading />;
  return (
    <div className="space-y-4">
      <Panel title="Sales by team member" testid="team-reports">
        {rows.length === 0 ? <Empty icon={BarChart3} title="No team data available" /> : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 border-b"><th className="py-2 font-medium">Member</th><th className="font-medium">Role</th><th className="text-right font-medium">Deals</th><th className="text-right font-medium">Won</th><th className="text-right font-medium">Won value</th><th className="text-right font-medium">Open value</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.crm_user_id} className="border-b border-slate-50" data-testid={`team-report-row-${r.crm_user_id}`}>
                <td className="py-2.5 font-medium">{r.name}</td><td className="text-slate-500">{ROLE_LABELS[r.crm_role]}</td><td className="text-right">{r.deals}</td><td className="text-right">{r.won_count}</td><td className="text-right">{gbp(r.won_value)}</td><td className="text-right">{gbp(r.open_value)}</td>
              </tr>))}</tbody>
          </table></div>
        )}
      </Panel>
      <Panel title="HR reports" testid="hr-reports-phase2"><Empty title="Coming in Phase 2" hint="Attendance and leave reports will be available once those modules launch." /></Panel>
    </div>
  );
};

export default function ReportsPage() {
  const { tab = "my" } = useParams();
  const { isManagerUp } = useWorkspace();
  const tabs = [{ key: "my", label: "My Reports", to: "/crm-app/reports/my" }, ...(isManagerUp ? [{ key: "team", label: "Team Reports", to: "/crm-app/reports/team" }] : [])];
  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Sales performance from your live CRM data" />
      <SubTabs tabs={tabs} />
      {tab === "team" && isManagerUp ? <Team /> : <My />}
    </div>
  );
}
